/**
 * ClaimConfirmDialog — W1.7
 *
 * Claim-Details + Steuer-Disclaimer.
 * Amount-Validierung: positiv, <= maxAmount.
 *
 * AC-3: Token-Typ, Amount-Input, Pflicht-Steuerhinweis, CTA.
 * AC-4: Submit sendet POST /api/v2/wallets/:id/claim mit X-Reauth-Token-Header.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { claimApi } from './api/claimApi'

type Props = {
  walletId: string
  tokenType: string
  maxAmount: number
  reauthToken: string
  onSubmitted: (claimId: string) => void
  onCancel: () => void
  onReauthExpired: () => void
  onUpgradeRequired: () => void
}

export function ClaimConfirmDialog({
  walletId,
  tokenType,
  maxAmount,
  reauthToken,
  onSubmitted,
  onCancel,
  onReauthExpired,
  onUpgradeRequired,
}: Props) {
  const { t } = useTranslation('claims')
  const [amount, setAmount] = useState(maxAmount)
  const [amountError, setAmountError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  function validateAmount(value: number): string | null {
    if (value <= 0) return t('confirmDialog.errors.amountTooLow')
    if (value > maxAmount) return t('confirmDialog.errors.amountTooHigh', { maxAmount })
    return null
  }

  function handleAmountChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = parseFloat(e.target.value)
    setAmount(isNaN(val) ? 0 : val)
    setAmountError(validateAmount(isNaN(val) ? 0 : val))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const err = validateAmount(amount)
    if (err) {
      setAmountError(err)
      return
    }

    setSubmitError(null)
    setIsLoading(true)

    const { data, error } = await claimApi.startClaim(walletId, reauthToken, amount, tokenType)
    setIsLoading(false)

    if (error) {
      const status = (error as { status?: number }).status
      if (status === 402) {
        onUpgradeRequired()
        return
      }
      if (status === 403) {
        onReauthExpired()
        return
      }
      if (status === 422) {
        const errObj = error as { message?: string }
        setSubmitError(errObj.message ?? t('confirmDialog.errors.validation'))
        return
      }
      setSubmitError(t('confirmDialog.errors.unknown'))
      return
    }

    if (data?.claim_id) {
      onSubmitted(data.claim_id)
    }
  }

  return (
    <div
      className="drawer-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="claim-confirm-title"
    >
      <div className="card claim-confirm-dialog">
        <h2 id="claim-confirm-title">{t('confirmDialog.title', { tokenType: tokenType.toUpperCase() })}</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-field">
            <label className="label" htmlFor="claim-amount">
              {t('confirmDialog.amountLabel', { tokenType: tokenType.toUpperCase() })}
            </label>
            <input
              id="claim-amount"
              type="number"
              step="any"
              min="0"
              max={maxAmount}
              className="input"
              value={amount}
              onChange={handleAmountChange}
              disabled={isLoading}
            />
            {amountError && (
              <p className="inline-error" role="alert">
                {amountError}
              </p>
            )}
          </div>

          <div className="hint tax-disclaimer" role="note">
            {t('confirmDialog.taxDisclaimer')}
          </div>

          {submitError && (
            <p className="inline-error" role="alert" aria-live="assertive">
              {submitError}
            </p>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={isLoading}
            >
              {t('common:actions.cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading || !!amountError || amount <= 0}
            >
              {isLoading ? t('confirmDialog.actions.submitting') : t('confirmDialog.actions.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
